import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { FixedAsset } from '@/hooks/useFixedAssets';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

interface FixedAssetsDistributionChartProps {
  fixedAssets: FixedAsset[];
}

export function FixedAssetsDistributionChart({ fixedAssets }: FixedAssetsDistributionChartProps) {
  const chartData = useMemo(() => {
    if (!fixedAssets || fixedAssets.length === 0) return [];

    const distribution = fixedAssets.reduce((acc, asset) => {
      // Only include owned assets in the distribution
      if (asset.status !== 'owned') return acc;
      
      const type = asset.asset_type.replace(/_/g, ' ');
      const value = Number(asset.value) || 0;
      
      const existing = acc.find(item => item.name === type);
      if (existing) {
        existing.value += value;
      } else {
        acc.push({ name: type, value });
      }
      return acc;
    }, [] as { name: string; value: number }[]);

    return distribution.sort((a, b) => b.value - a.value);
  }, [fixedAssets]);

  if (chartData.length === 0) {
    return null;
  }

  // Format value for tooltip
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: fixedAssets[0]?.currency || 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Card className="col-span-full">
      <CardHeader>
        <CardTitle>Asset Distribution</CardTitle>
        <CardDescription>Breakdown of your fixed assets by type</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
              <Legend verticalAlign="bottom" height={36} className="capitalize" />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
